import { ActionIcon, Badge, Box, Button, Divider, Grid, Group, LoadingOverlay, Menu, Paper, Popover, Progress, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { FaEllipsisV, FaExclamationTriangle, FaEye, FaFilter, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerAvatar from '../components/customer_avatar';
import { CustomerFlow } from '../components/customers/customers_flow';
import { CustomerModal } from '../components/customers/customers_modal';
import InputField from '../components/input_field';
import OAuthInfosModal from '../components/oauth/infos';
import CustomerAccountLinkModal from '../components/oauth/customer_account_link_modal';
import useOauth, { OAUTH } from '../components/oauth/oauth';
import SimpleHeader from '../components/simple_header';
import { useApp } from '../contexts/app.context';
import { useAuth } from '../contexts/auth.context';
import { CONNECT_CUSTOMER_OAUTH, DELETE_CUSTOMER } from '../services/customers';
import { DELETE_OAUTH, GET_ALL_OAUTHS } from '../services/oauths';
import { getConnectionDisplayTitle, getCustomerSocialConnections } from '../utility/customer_social';
import { getMedia, getOauthTitle, medias, parseKeyword } from '../utility/util';

export default function CustomersPage() {
    const [modal, setModal] = React.useState<any>(null);
    const [flow, setFlow] = React.useState<any>(null);
    const [infosOpen, setInfosOpen] = React.useState<any>(null);
    const [linkingData, setLinkingData] = React.useState<any>(null);
    const [loadingLinking, setLoadingLinking] = React.useState<boolean>(false);
    const [filters, setFilters] = React.useState<any>({
        status: true
    });
    const [loadingDelete, setLoadingDelete] = React.useState({});
    const theme = useMantineTheme();

    const { connect, loading: loadingOauth } = useOauth();

    const { confirmDialog, loadingCustomers, loadCustomers, customers: items } = useApp();
    const { userData } = useAuth();
    const navigate = useNavigate();
    const { customerId } = useParams();

    const disconnectOauth = (customerId, connection) => {
        if (!connection) {
            return;
        }

        if (connection.connectionMode === "legacy") {
            DELETE_OAUTH(connection.oauthId)
                .then(() => {
                    loadCustomers();
                })
                .catch(err => {
                    showNotification({ title: "Ops", message: err.message, color: 'red' })
                });
            return;
        }

        CONNECT_CUSTOMER_OAUTH(connection.origin, customerId, {
            unlink: true,
            oauthId: connection.oauthId,
            oauth_id: connection.oauthId,
            accountId: connection.accountId,
            account_id: connection.accountId,
            connectionId: connection.raw?._id,
            connection_id: connection.raw?._id,
        })
            .then(() => {
                loadCustomers();
            })
            .catch(err => {
                showNotification({ title: "Ops", message: err.message, color: 'red' })
            });
    }

    const bindCustomerAccount = async ({ customer, origin, oauth, account }) => {
        if (!customer?._id || !origin || !oauth?._id || !account?.id) {
            showNotification({ title: "Ops", message: "Selecione uma conta válida para vincular", color: 'red' });
            return;
        }

        setLoadingLinking(true);
        try {
            await CONNECT_CUSTOMER_OAUTH(origin, customer._id, {
                oauthId: oauth._id,
                oauth_id: oauth._id,
                accountId: account.id,
                account_id: account.id,
                accountTitle: account.title,
                account_title: account.title,
            });
            setLinkingData(null);
            loadCustomers();
        } catch (err: any) {
            showNotification({ title: "Ops", message: err.message, color: 'red' });
        } finally {
            setLoadingLinking(false);
        }
    }

    const startCustomerSocialConnection = async (customer, origin: OAUTH) => {
        if (!customer?._id) {
            return;
        }

        setLoadingLinking(true);
        try {
            const oauths = (await GET_ALL_OAUTHS()).filter((oauth) => oauth.origin === origin);

            if (oauths.length > 0) {
                setLinkingData({ customer, origin, oauths });
                return;
            }

            connect(origin, async (oauth) => {
                const updatedOauths = oauth ? [oauth] : (await GET_ALL_OAUTHS()).filter((item) => item.origin === origin);
                if (updatedOauths.length === 0) {
                    return;
                }

                setLinkingData({ customer, origin, oauths: updatedOauths });
            });
        } catch (err: any) {
            showNotification({ title: "Ops", message: err.message, color: 'red' });
        } finally {
            setLoadingLinking(false);
        }
    }

    const addCompanyOauthFromLinkingModal = async () => {
        if (!linkingData?.origin || !linkingData?.customer) {
            return;
        }

        setLoadingLinking(true);
        connect(linkingData.origin, async (oauth) => {
            try {
                const oauths = oauth
                    ? [oauth, ...(await GET_ALL_OAUTHS()).filter((item) => item.origin === linkingData.origin && item._id !== oauth._id)]
                    : (await GET_ALL_OAUTHS()).filter((item) => item.origin === linkingData.origin);

                setLinkingData((current) => current ? { ...current, oauths } : current);
            } catch (err: any) {
                showNotification({ title: "Ops", message: err.message, color: 'red' });
            } finally {
                setLoadingLinking(false);
            }
        });
    }

    const deleteItem = (customerId) => {
        setLoadingDelete(ld => ({ ...ld, [`${customerId}`]: true }))
        DELETE_CUSTOMER(customerId)
            .then(res => {
                setLoadingDelete(ld => ({ ...ld, [`${customerId}`]: false }))
                loadCustomers()
            })
            .catch(err => {
                setLoadingDelete(ld => ({ ...ld, [`${customerId}`]: false }))
                showNotification({ title: "Ops", message: err.message, color: 'red' })
            })
    }

    const customersJobs = useMemo(() => {
        const hashMap = new Map<string, any[]>();
        for (let item of items) {
            hashMap.set(item._id, item.jobs ?? [])
        }
        return hashMap;
    }, [items]);

    useEffect(loadCustomers, []);

    useEffect(() => {
        const existent = items.find(i => customerId === i._id);
        if (existent || customerId === "novo") setModal(m => m ? m : customerId === "novo" ? {} : existent);
    }, [items, customerId]);

    return <>
        <SimpleHeader title="Clientes"
            right={<Group>
                <InputField
                    placeholder='Buscar cliente...'
                    name="keyword"
                    size="md"
                    value={filters.keyword}
                    onChange={({ keyword }) => setFilters(f => ({ ...f, keyword }))}
                />
                <Menu position='bottom'>
                    <Menu.Target>
                        <Button size="md" variant='white' leftIcon={<FaFilter />}>{
                            filters.status === null ? "Todos" : filters.status ? "Ativos" : "Inativos"
                        }</Button>
                    </Menu.Target>
                    <Menu.Dropdown style={{ maxWidth: 300, minWidth: 300 }}>
                        <Menu.Item onClick={() => { setFilters(f => ({ ...f, status: null })); }}>Ver Todos</Menu.Item>
                        <Menu.Item onClick={() => { setFilters(f => ({ ...f, status: true })); }}>Ativos</Menu.Item>
                        <Menu.Item onClick={() => { setFilters(f => ({ ...f, status: false })); }}>Inativos</Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>}
            buttons={[
                { leftIcon: <FaPlus />, title: "Cliente", onClick: () => { setModal(true) } },
            ]}
        />

        <LoadingOverlay visible={loadingCustomers} />

        <Grid>
            {items.length === 0 && <Grid.Col xs={12} md={12}><Paper p="md"><Text align="center">Nenhum cliente cadastrado</Text></Paper></Grid.Col>}
            {items.filter(i => (
                (filters.status === null || i.active === filters.status) &&
                (parseKeyword(i.name ?? "").includes(parseKeyword(filters.keyword ?? "")))
            )).map((item, i) => {
                const qtToApprove = ((customersJobs.get(item._id) ?? []).filter(j => ["draft", "to-approve"].includes(j.statusKey)).length);
                const qtAdjust = ((customersJobs.get(item._id) ?? []).filter(j => ["adjust"].includes(j.statusKey)).length)
                const qtApproved = ((customersJobs.get(item._id) ?? []).filter(j => ["approved"].includes(j.statusKey)).length)

                const customerConnections = getCustomerSocialConnections(item);
                return (
                    <Grid.Col xs={12} md={3}>
                        <Paper shadow='xs' p="0">
                            <Box p="md">
                                <Group align='center'>
                                    <div style={{ flex: 1 }}>
                                        <CustomerAvatar image={item.image} name={item.name} />
                                    </div>
                                    <Menu>
                                        <Menu.Target>
                                            <ActionIcon color="gray"><FaEllipsisV /></ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown style={{ minWidth: 140 }}>
                                            <Menu.Item
                                                color={"blue"}
                                                icon={<FaEye />}
                                                onClick={() => { navigate(`/jobs?cliente=${item._id}`) }}
                                            >Ver Jobs</Menu.Item>
                                            <Menu.Item
                                                color={"green"}
                                                icon={<FaPlus />}
                                                onClick={() => { navigate(`/jobs/novo?customer=${item._id}`) }}
                                            >Novo Job</Menu.Item>

                                            <Menu.Divider />

                                            <Menu.Item
                                                color={"gray"}
                                                icon={<FaPencilAlt />}
                                                onClick={() => { setModal(item) }}
                                            >Editar</Menu.Item>
                                            <Menu.Item
                                                color={"gray"}
                                                icon={<FaPencilAlt />}
                                                onClick={() => { setFlow(item) }}
                                            >Fluxo de Aprovação</Menu.Item>

                                            <Menu.Divider />

                                            <Menu.Item
                                                color={"red"}
                                                icon={<FaTrash />}
                                                onClick={() => {
                                                    confirmDialog(
                                                        { text: "Realmente deseja deletar?" },
                                                        (({ confirmed }) => {
                                                            if (confirmed) deleteItem(item._id);
                                                        })
                                                    )
                                                }}
                                            >Inativar</Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </Group>
                                <Divider mt="lg" mb="lg" />
                                <UnstyledButton style={{ width: '100%' }} onClick={() => navigate(`/jobs?cliente=${item._id}`)}>
                                    <Text color="gray" weight="bold">{(customersJobs.get(item._id) ?? []).length} jobs realizados</Text>
                                    <Progress
                                        size="xl"
                                        sections={[
                                            {
                                                label: "Aguardando aprovação", value: (customersJobs.get(item._id) ?? []).length === 0
                                                    ? 0
                                                    : (qtToApprove * 100) / (customersJobs.get(item._id) ?? []).length, color: "gray",
                                                tooltip: `${qtToApprove} - Aguardando aprovação`
                                            },
                                            {
                                                label: "Em Ajuste", value: (customersJobs.get(item._id) ?? []).length === 0
                                                    ? 0
                                                    : (qtAdjust * 100) / (customersJobs.get(item._id) ?? []).length, color: "yellow",
                                                tooltip: `${qtAdjust} - Em Ajuste`
                                            },
                                            {
                                                label: "Aprovados", value: (customersJobs.get(item._id) ?? []).length === 0
                                                    ? 0
                                                    : (qtApproved * 100) / (customersJobs.get(item._id) ?? []).length, color: "green",
                                                tooltip: `${qtApproved} - Aprovados`
                                            },
                                        ]}
                                    />
                                </UnstyledButton>
                                <Group mt="md" spacing={4}>
                                    {item.active ? <Badge variant="filled" color="green">Ativo</Badge> : <Badge variant="filled" color="red">Inativo</Badge>}
                                    {
                                        !(item.flow ?? []).some(f => (f.approvers ?? []).some(a => a.email || a.phone)) &&
                                        <UnstyledButton
                                            onClick={() => setFlow(item)}>
                                            <Badge
                                                color="yellow"
                                                variant="filled"
                                                leftSection={<FaExclamationTriangle />}
                                            >FLUXO DE APROVAÇÃO</Badge>
                                        </UnstyledButton>
                                    }
                                </Group>
                            </Box>

                            <Group spacing={0} style={{ borderTop: '1px solid #DFDFDF' }}>
                                {
                                    customerConnections
                                        .sort((a, b) => getMedia(a.origin)?.title > getMedia(b.origin)?.title ? 1 : -1)
                                        .map((connection) => {
                                        const media = getMedia(connection.origin);
                                        if (!media) return null;
                                        const { Icon } = media;
                                        return <Popover>
                                            <Popover.Target>
                                                <ActionIcon
                                                    variant='filled'
                                                    color={media?.color}
                                                    size="lg"
                                                    style={{ borderRadius: 0, borderRight: '1px solid #CACACA' }}
                                                >
                                                    <Icon />
                                                </ActionIcon>
                                            </Popover.Target>
                                            <Popover.Dropdown
                                                p="8px 20px"
                                                style={{
                                                    backgroundColor: theme.colors[media.color][8],
                                                }}
                                            >
                                                <Group>
                                                    <Icon color="white" />
                                                    <Box style={{ flex: 1 }}>
                                                        <Text ta="center" c="white" size="sm">{getConnectionDisplayTitle(connection, getOauthTitle(connection.oauth ?? { origin: connection.origin, data: { me: {} } }))}</Text>
                                                        {
                                                            <Text ta="center" c="white" size="10px">
                                                                {
                                                                    !connection.infinite_renew && connection?.auth_expires_at
                                                                        ? `Expira ${moment(connection?.auth_expires_at).format("DD/MM/YYYY")}`
                                                                        : `Conectado`
                                                                }
                                                            </Text>
                                                        }
                                                    </Box>
                                                </Group>
                                                <Button mt="xs" variant='subtle' c="white" fullWidth onClick={() => { disconnectOauth(item._id, connection); }} size="xs">Desconectar</Button>
                                            </Popover.Dropdown>
                                        </Popover>
                                    })
                                }
                                <div style={{ flex: 1 }}></div>
                                {userData?.allow_schedule_content && <>
                                    <Menu>
                                        <Menu.Target>
                                            {/* <Button
                                                size="md"
                                                fullWidth
                                                variant='light'
                                                color="gray"
                                                leftIcon={<FaPlus />}
                                            >Conectar Rede Social</Button> */}
                                            <ActionIcon
                                                size="lg"
                                                variant='light'
                                                color="gray"
                                                style={{ borderRadius: 0 }}
                                            ><FaPlus /></ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            {
                                                medias
                                                    .filter(m => [
                                                        "instagram",
                                                        "twitter",
                                                        "pinterest",
                                                        "facebook",
                                                        "linkedin",
                                                        "youtube",
                                                        "tiktok",
                                                        "google_business",
                                                    ].includes(m.key))
                                                    .map(({ Icon, title, key, color }) => (
                                                        <Menu.Item
                                                            color={color}
                                                            icon={<Icon />}
                                                            onClick={() => {
                                                                if (key === OAUTH.INSTAGRAM) setInfosOpen({ key, customer: item });
                                                                else startCustomerSocialConnection(item, key as OAUTH);
                                                            }}
                                                        >{title}</Menu.Item>
                                                    ))
                                            }
                                        </Menu.Dropdown>
                                    </Menu>
                                </>}
                            </Group>
                        </Paper>
                    </Grid.Col>
                )
            })}
        </Grid>

        <OAuthInfosModal
            data={infosOpen}
            onClose={() => setInfosOpen(null)}
            onConnect={() => {
                startCustomerSocialConnection(infosOpen.customer, infosOpen.key);
                setInfosOpen(null);
            }}
        />

        <CustomerAccountLinkModal
            data={linkingData ? {
                customerName: linkingData?.customer?.name,
                origin: linkingData?.origin,
                oauths: linkingData?.oauths,
            } : null}
            loading={loadingLinking}
            loadingAddConnection={loadingOauth}
            onClose={() => setLinkingData(null)}
            onAddConnection={addCompanyOauthFromLinkingModal}
            onConfirm={({ oauth, account }) => bindCustomerAccount({
                customer: linkingData?.customer,
                origin: linkingData?.origin,
                oauth,
                account,
            })}
        />

        <CustomerModal
            data={modal}
            opened={!!modal}
            onClose={() => setModal(null)}
            onSave={(res) => { loadCustomers(); navigate("/clientes"); setFlow(!modal?._id ? res : null); setModal(null); }}
        />

        <CustomerFlow
            data={flow}
            opened={!!flow}
            onClose={() => setFlow(null)}
            onSave={() => { loadCustomers(); navigate("/clientes"); setFlow(null) }}
        />

        <LoadingOverlay visible={loadingOauth} />
    </>
}
